import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { DOCUMENT, pageShellSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from './PartnerListChrome';
import { getGalleryUrls, getPackageCoverSrc } from '../utils/partnerListUtils';
import { resolvePrivateJourneyPriceDisplay } from '../utils/privateJourneyPricing';

const sectionTitleSx = {
  fontFamily: DOCUMENT.display,
  fontSize: { xs: 24, md: 28 },
  fontWeight: 400,
  pb: 1.25,
  mb: 2,
  borderBottom: `1px solid ${DOCUMENT.ink}`,
  color: DOCUMENT.ink,
};

const navLinkSx = {
  fontSize: 11,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink2,
  textDecoration: 'none',
  borderBottom: `1px solid transparent`,
  pb: 0.5,
  '&:hover': { color: DOCUMENT.oxblood, borderBottomColor: DOCUMENT.oxblood },
};

const statusChipSx = {
  display: 'inline-block',
  px: 0.75,
  py: '1px',
  border: `1px solid ${DOCUMENT.oxblood}`,
  color: DOCUMENT.oxblood,
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  mr: 1,
};

const SECTIONS = [
  { id: 'overview', labelKey: 'privateJourney.navOverview' },
  { id: 'day-by-day', labelKey: 'privateJourney.navItinerary' },
  { id: 'services', labelKey: 'privateJourney.navServices' },
  { id: 'stay-travel', labelKey: 'privateJourney.navStay' },
  { id: 'included', labelKey: 'privateJourney.navIncluded' },
  { id: 'gallery', labelKey: 'privateJourney.navGallery' },
  { id: 'details', labelKey: 'privateJourney.navDetails' },
];

const DEFAULT_FAQ = [
  {
    q: 'Is this a confirmed trip I can book now?',
    a: 'No. This is a sample journey. Availability, suppliers, and prices are confirmed only after a private quote.',
  },
  {
    q: 'Can we customize the itinerary?',
    a: 'Yes. Share dates, group size, and preferences in your request. A specialist will shape a proposal for your group.',
  },
  {
    q: 'Who collects payment?',
    a: 'Under the partner-collects model, the fulfilling operator typically collects payment after terms are agreed. Submission of a request does not charge you.',
  },
];

const groupItemsByDay = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    const day = Number(item.day_number) || 1;
    if (!map.has(day)) map.set(day, []);
    map.get(day).push(item);
  });
  return [...map.entries()].sort((a, b) => a[0] - b[0]);
};

const PrivateJourneyPackageView = ({
  pkg,
  t,
  onRequestQuote,
  onCustomize,
}) => {
  const [activeGallery, setActiveGallery] = useState(0);
  const price = useMemo(() => resolvePrivateJourneyPriceDisplay(pkg), [pkg]);
  const gallery = useMemo(() => {
    const urls = getGalleryUrls(pkg);
    const cover = getPackageCoverSrc(pkg);
    const list = urls.length ? urls : (cover ? [cover] : []);
    return list;
  }, [pkg]);
  const days = useMemo(() => groupItemsByDay(pkg.items || []), [pkg.items]);
  const highlights = pkg.highlights || pkg.luxury_content?.highlights || [];
  const services = pkg.services || pkg.luxury_content?.services || [];
  const stayTravel = pkg.stay_travel || pkg.luxury_content?.stayTravel || null;
  const faq = (pkg.faq && pkg.faq.length) ? pkg.faq : DEFAULT_FAQ;
  const inclusions = Array.isArray(pkg.luxury_content?.inclusions)
    ? pkg.luxury_content.inclusions
    : [];
  const exclusions = Array.isArray(pkg.luxury_content?.exclusions)
    ? pkg.luxury_content.exclusions
    : [];
  const subjectToConfirmation = Array.isArray(pkg.luxury_content?.subjectToConfirmation)
    ? pkg.luxury_content.subjectToConfirmation
    : [];

  const hero = gallery[activeGallery] || gallery[0];

  return (
    <Box sx={{ ...pageShellSx, pb: { xs: 12, md: 6 } }} data-testid="private-journey-package-view">
      <Box sx={{ position: 'relative', minHeight: { xs: 280, md: 420 }, bgcolor: DOCUMENT.nav }}>
        {hero ? (
          <Box
            component="img"
            src={hero}
            alt={pkg.title}
            sx={{ width: '100%', height: { xs: 280, md: 420 }, objectFit: 'cover', display: 'block' }}
          />
        ) : null}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)',
          }}
        />
        <Box sx={{ position: 'absolute', left: { xs: 16, md: 40 }, bottom: { xs: 20, md: 36 }, right: 16, color: DOCUMENT.ivory }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
            <Box component="span" sx={{ ...statusChipSx, borderColor: DOCUMENT.accent, color: DOCUMENT.accent }}>
              {t('privateJourney.sampleBadge')}
            </Box>
            {pkg.package_type ? (
              <Box component="span" sx={{ ...statusChipSx, borderColor: DOCUMENT.ivory, color: DOCUMENT.ivory }}>
                {pkg.package_type}
              </Box>
            ) : null}
          </Box>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 42 }, fontWeight: 400, lineHeight: 1.1 }}>
            {pkg.title}
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 14, opacity: 0.9 }}>
            {[
              pkg.destination,
              pkg.duration_nights != null ? t('privateJourney.nightsDays', { nights: pkg.duration_nights, days: pkg.duration_days }) : null,
              pkg.min_travelers && pkg.max_travelers
                ? t('privateJourney.guestsRange', { min: pkg.min_travelers, max: pkg.max_travelers })
                : null,
            ].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: DOCUMENT.surface,
          borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
          px: { xs: 2, md: 4 },
          py: 2.5,
        }}
      >
        <Typography sx={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
          {t('privateJourney.curatedExample')}
        </Typography>
        <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 22, md: 26 }, mb: 0.5 }}>
          {price?.headline || t('privateJourney.bespokePricing')}
        </Typography>
        {price?.subline ? (
          <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, mb: 0.5 }}>{price.subline}</Typography>
        ) : null}
        {price?.assumptions ? (
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>{price.assumptions}</Typography>
        ) : (
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>
            {t('privateJourney.notInstantBooking')}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <Button onClick={onRequestQuote} sx={partnerOxbloodBtnSx} data-testid="private-journey-request-quote">
            {t('privateJourney.requestQuote')}
          </Button>
          {onCustomize ? (
            <Button onClick={onCustomize} sx={partnerOutlineBtnSx} data-testid="private-journey-customize">
              {t('privateJourney.customize')}
            </Button>
          ) : null}
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          gap: 2.5,
          flexWrap: 'wrap',
          px: 4,
          py: 1.75,
          borderBottom: `1px solid ${DOCUMENT.rule}`,
          position: 'sticky',
          top: 0,
          bgcolor: DOCUMENT.canvas,
          zIndex: 2,
        }}
      >
        {SECTIONS.map((section) => (
          <Box
            key={section.id}
            component="a"
            href={`#${section.id}`}
            sx={navLinkSx}
          >
            {t(section.labelKey)}
          </Box>
        ))}
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 }, maxWidth: 920 }}>
        <Box id="overview" sx={{ mb: 5 }}>
          <Typography sx={sectionTitleSx}>{t('privateJourney.overview')}</Typography>
          <Typography sx={{ color: DOCUMENT.ink2, fontSize: 15.5, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {pkg.description || pkg.short_description}
          </Typography>
          {highlights.length ? (
            <Box sx={{ mt: 3, display: 'grid', gap: 1.5 }}>
              {highlights.map((item) => (
                <Box key={item.title} sx={{ border: `1px solid ${DOCUMENT.rule}`, p: 2, bgcolor: DOCUMENT.surface }}>
                  <Box sx={{ mb: 0.75 }}>
                    <Box component="span" sx={statusChipSx}>{item.status || 'proposed'}</Box>
                  </Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{item.title}</Typography>
                  <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, mt: 0.5 }}>{item.detail}</Typography>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>

        <Box id="day-by-day" sx={{ mb: 5 }}>
          <Typography sx={sectionTitleSx}>{t('privateJourney.itinerary')}</Typography>
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>
            {t('privateJourney.itineraryDisclaimer')}
          </Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {days.map(([dayNum, dayItems]) => {
              const primary = dayItems[0];
              return (
                <Box key={dayNum} sx={{ borderLeft: `2px solid ${DOCUMENT.oxblood}`, pl: 2, py: 0.5 }}>
                  <Typography sx={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: DOCUMENT.accent, mb: 0.5 }}>
                    {t('privateJourney.dayLabel', { day: dayNum })}
                  </Typography>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 0.5 }}>
                    {primary.title || primary.name}
                  </Typography>
                  {primary.location ? (
                    <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 0.75 }}>{primary.location}</Typography>
                  ) : null}
                  <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.6 }}>
                    {primary.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box id="services" sx={{ mb: 5 }}>
          <Typography sx={sectionTitleSx}>{t('privateJourney.services')}</Typography>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            {services.map((svc) => (
              <Box key={`${svc.category}-${svc.description}`} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box component="span" sx={statusChipSx}>{svc.status || 'proposed'}</Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{svc.category}</Typography>
                  <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14 }}>{svc.description}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="stay-travel" sx={{ mb: 5 }}>
          <Typography sx={sectionTitleSx}>{t('privateJourney.stayTravel')}</Typography>
          <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
            {stayTravel || t('privateJourney.stayTravelFallback')}
          </Typography>
        </Box>

        <Box id="included" sx={{ mb: 5 }}>
          <Typography sx={sectionTitleSx}>{t('privateJourney.included')}</Typography>
          {inclusions.length ? (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
                {t('privateJourney.proposedIncluded')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: DOCUMENT.ink2 }}>
                {inclusions.map((line) => <li key={line}>{line}</li>)}
              </Box>
            </Box>
          ) : null}
          {exclusions.length ? (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
                {t('privateJourney.excluded')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: DOCUMENT.ink2 }}>
                {exclusions.map((line) => <li key={line}>{line}</li>)}
              </Box>
            </Box>
          ) : null}
          {subjectToConfirmation.length ? (
            <Box>
              <Typography sx={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 1 }}>
                {t('privateJourney.subjectToConfirmation')}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: DOCUMENT.ink2 }}>
                {subjectToConfirmation.map((line) => <li key={line}>{line}</li>)}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14 }}>
              {t('privateJourney.inclusionsFallback')}
            </Typography>
          )}
        </Box>

        <Box id="gallery" sx={{ mb: 5 }}>
          <Typography sx={sectionTitleSx}>{t('privateJourney.gallery')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1.25 }}>
            {gallery.map((url, idx) => (
              <Box
                key={`${url}-${idx}`}
                component="button"
                type="button"
                onClick={() => setActiveGallery(idx)}
                sx={{
                  p: 0,
                  border: idx === activeGallery ? `2px solid ${DOCUMENT.oxblood}` : `1px solid ${DOCUMENT.rule}`,
                  cursor: 'pointer',
                  bgcolor: DOCUMENT.nav,
                }}
              >
                <Box component="img" src={url} alt="" sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
              </Box>
            ))}
          </Box>
        </Box>

        <Box id="details" sx={{ mb: 4 }}>
          <Typography sx={sectionTitleSx}>{t('privateJourney.faq')}</Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {faq.map((item) => (
              <Box key={item.q || item.question}>
                <Typography sx={{ fontWeight: 600, fontSize: 14.5, mb: 0.5 }}>{item.q || item.question}</Typography>
                <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, lineHeight: 1.6 }}>{item.a || item.answer}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          p: 1.5,
          bgcolor: DOCUMENT.surface,
          borderTop: `1px solid ${DOCUMENT.ruleHard}`,
          zIndex: 10,
        }}
      >
        <Button fullWidth onClick={onRequestQuote} sx={partnerOxbloodBtnSx}>
          {t('privateJourney.requestQuote')}
        </Button>
      </Box>
    </Box>
  );
};

export default PrivateJourneyPackageView;
